import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type LocationState = {
  pathname: string;
  search: string;
  hash: string;
};

const readLocation = (): LocationState => ({
  pathname: window.location.pathname || '/',
  search: window.location.search || '',
  hash: window.location.hash || '',
});

const LocationContext = createContext<LocationState | null>(null);

export const Router: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location, setLocation] = useState<LocationState>(() => readLocation());

  useEffect(() => {
    const handlePopState = () => setLocation(readLocation());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <LocationContext.Provider value={location}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationState => {
  const location = useContext(LocationContext);
  return location || readLocation();
};

export const Link: React.FC<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }
> = ({ to, onClick, children, ...props }) => {
  const [, forceRender] = useState(0);

  const handleClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      props.target
    ) {
      return;
    }

    event.preventDefault();
    if (window.location.pathname + window.location.search + window.location.hash !== to) {
      window.history.pushState({}, '', to);
      window.dispatchEvent(new PopStateEvent('popstate'));
      forceRender(value => value + 1);
    }
  }, [onClick, props.target, to]);

  return (
    <a href={to} onClick={handleClick} {...props}>
      {children}
    </a>
  );
};

type RouteProps = {
  path: string;
  element: React.ReactElement;
};

export const Route: React.FC<RouteProps> = () => null;

export const Routes: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const routes = useMemo(
    () => React.Children.toArray(children).filter(React.isValidElement<RouteProps>),
    [children],
  );
  const match = routes.find(route => route.props.path === location.pathname) || routes[0];
  return match?.props.element ?? null;
};
